import { FC, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import { useGetList, useTranslate } from 'react-admin'
import { GemeenteResponse } from '../../types/apiRequests'
import { getUrl } from '../../providers/data'
import authToken from '../../utils/authToken'

const CbsDataDownloadSection: FC = () => {
  const translate = useTranslate()
  const { data: gemeenten, isLoading: gemeentenLoading } = useGetList<GemeenteResponse>(
    'gemeenten',
    {
      pagination: { page: 1, perPage: 99 },
      sort: { field: 'naam', order: 'ASC' },
    }
  )

  const [gemeenteSid, setGemeenteSid] = useState<number | ''>('')
  const [jaren, setJaren] = useState('')
  const [toonInfo, setToonInfo] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [error, setError] = useState('')

  const handleDownload = async () => {
    if (gemeenteSid === '') return
    setDownloading(true)
    setError('')

    try {
      const params = new URLSearchParams()
      if (jaren.trim()) params.set('jaren', jaren.trim())
      params.set('toon_info', String(toonInfo))

      // authToken.getToken() already returns the value prefixed with "Bearer ".
      const token = authToken.getToken() || ''
      const response = await fetch(
        `${getUrl()}/admin/cbs/gemeenten/${gemeenteSid}/download?${params.toString()}`,
        { headers: { 'Authorization': token, 'accept-language': 'nl' } }
      )

      if (!response.ok) {
        let detail = `HTTP ${response.status}`
        try {
          const body = (await response.json()) as { detail?: unknown }
          if (body.detail !== undefined) detail = JSON.stringify(body.detail)
        } catch {
          // body wasn't JSON — keep the HTTP code message
        }
        throw new Error(detail)
      }

      const blob = await response.blob()
      const selected = gemeenten?.find((g) => g.sid === gemeenteSid)
      const filename = `cbs-data-${selected?.gm_code || gemeenteSid}.zip`

      const downloadUrl = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = downloadUrl
      anchor.download = filename
      anchor.click()
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Onbekende fout'
      setError(translate('settings.cbs.error', { message }))
    } finally {
      setDownloading(false)
    }
  }

  return (
    <Paper sx={{ p: 3, mt: 2 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {translate('settings.cbs.title')}
      </Typography>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        {translate('settings.cbs.description')}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
        <FormControl size="small" fullWidth>
          <InputLabel>{translate('settings.cbs.gemeente')}</InputLabel>
          <Select
            value={gemeenteSid}
            label={translate('settings.cbs.gemeente')}
            onChange={(e) => setGemeenteSid(Number(e.target.value))}
            disabled={gemeentenLoading || downloading}
          >
            {gemeenten?.map((g) => (
              <MenuItem key={g.id} value={g.sid}>
                {g.naam} ({g.gm_code})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          label={translate('settings.cbs.jaren')}
          placeholder={translate('settings.cbs.jaren_placeholder')}
          value={jaren}
          onChange={(e) => setJaren(e.target.value)}
          disabled={downloading}
          helperText={translate('settings.cbs.jaren_help')}
          fullWidth
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={toonInfo}
              onChange={(e) => setToonInfo(e.target.checked)}
              disabled={downloading}
            />
          }
          label={translate('settings.cbs.toon_info')}
        />
      </Box>

      {downloading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {translate('settings.cbs.building_zip')}
          </Typography>
          <LinearProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        onClick={() => void handleDownload()}
        variant="contained"
        startIcon={<DownloadIcon />}
        disabled={gemeenteSid === '' || downloading}
      >
        {translate('settings.cbs.download_button')}
      </Button>
    </Paper>
  )
}

export default CbsDataDownloadSection
